import {
  EnterpriseCheckoutLayoutGrid,
  OrderCard,
  OrderCardTitle,
  PaymentCard,
  PbLabel,
  PbRow,
  PbVal,
  SpecPills,
} from 'pages/Subscriptions/SubscriptionInner/styled';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { breakpoints } from 'theme';

/** Payment form first on narrow viewports so the CTA is above the fold. */
export const EnterpriseCheckoutGrid = styled(EnterpriseCheckoutLayoutGrid)`
  @media ${breakpoints.downMdPlus} {
    > ${PaymentCard} {
      order: -1;
    }
  }
`;

export const EnterpriseOrderColumn = styled.div`
  width: 100%;
  min-width: 0;
  text-align: left;
`;

export const EnterpriseOrderCard = styled(OrderCard)`
  text-align: left;

  ${SpecPills} {
    justify-content: flex-start;
  }
`;

export const EnterpriseOrderCardTitle = styled(OrderCardTitle)`
  font-weight: 700;
  text-align: left;
`;

/** Enterprise invoice total: mock uses 15px value, not the 19px used for currency totals */
export const EnterpriseOrderPbRowTotal = styled(PbRow)`
  font-size: 15px;
  margin-top: 4px;

  ${PbLabel} {
    color: ${({ theme }) => theme.color.pickledBluewood};
    font-weight: 600;
  }

  ${PbVal} {
    font-size: 15px;
    font-weight: 700;
  }
`;

export const ConfirmWrap = styled.div`
  max-width: 620px;
  width: 100%;
  margin: 0 auto;
  padding: 0 4px;
  box-sizing: border-box;
  text-align: center;

  @media ${breakpoints.downSmPlus} {
    max-width: 100%;
    padding: 0;
  }
`;

export const ConfirmCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.aquaSqueeze};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 36px;

  @media ${breakpoints.downSmPlus} {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
    font-size: 28px;
  }
`;

export const ConfirmTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};

  @media ${breakpoints.downSmPlus} {
    font-size: 22px;
  }
`;

export const ConfirmSub = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.color.BlueBayoux};

  @media ${breakpoints.downSmPlus} {
    margin-bottom: 20px;
    font-size: 13px;
  }
`;

export const ConfirmCard = styled.div`
  background: ${({ theme }) => theme.color.aquaSqueeze};
  border: 1.5px solid ${({ theme }) => theme.color.botticelli};
  border-radius: 20px;
  padding: 22px;
  text-align: left;
  margin-bottom: 20px;

  @media ${breakpoints.downSmPlus} {
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 16px;
  }
`;

export const ConfirmCardTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.skobeloffGreen};
`;

export const ConfirmList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ConfirmLi = styled.li`
  font-size: 13px;
  color: ${({ theme }) => theme.color.BlueBayoux};
  padding: 4px 0;
  display: flex;
  gap: 8px;
  line-height: 1.5;
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

/** Cal.com book-a-call row (same interaction pattern as onboarding checkout success). */
export const CalScheduleWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  width: 100%;

  @media ${breakpoints.downSmPlus} {
    margin-bottom: 12px;
  }
`;

const enterpriseCalSchedulePulse = keyframes`
  0%,
  100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgb(11 60 50 / 35%);
  }
  50% {
    opacity: 0.88;
    box-shadow: 0 0 0 8px rgb(11 60 50 / 0%);
  }
`;

export const BtnEnterpriseCalSchedule = styled.button<{
  $pulsing?: boolean;
}>`
  padding: 13px 32px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: background 0.15s ease;
  max-width: 100%;
  box-sizing: border-box;

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    padding: 14px 20px;
  }

  ${({ $pulsing = true }) =>
    $pulsing
      ? css`
          animation: ${enterpriseCalSchedulePulse} 1.5s ease-in-out infinite;
        `
      : css`
          animation: none;
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  &:hover {
    animation: none;
    opacity: 1;
    box-shadow: none;
    background: #0a1a14;
  }
`;

export const ConfirmActionsBtns = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

export const ConfirmSecondaryLink = styled(Link)`
  padding: 13px 32px;
  border-radius: 40px;
  background: transparent;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  max-width: 100%;

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    padding: 14px 20px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.color.skobeloffGreen};
    color: ${({ theme }) => theme.color.skobeloffGreen};
  }
`;

export const GoDashboardBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 13px 32px;
  border-radius: 40px;
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border: none;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.15s;
  box-sizing: border-box;
  max-width: 100%;

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    padding: 14px 20px;
  }

  &:hover {
    filter: brightness(1.05);
  }
`;

export const OrgFieldWrap = styled.div`
  text-align: left;
  margin: 0 0 16px;
`;

export const OrgLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const OrgInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 10px;
  border: 1.5px solid ${({ theme }) => theme.color.porcelain};
  color: ${({ theme }) => theme.color.pickledBluewood};
  background: ${({ theme }) => theme.color.white};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const EnterpriseInfoBox = styled.div`
  background: ${({ theme }) => theme.color.skobeloffGreen};
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin-top: 14px;

  strong {
    color: #fff;
    display: block;
    font-size: 13px;
    margin-bottom: 4px;
  }
`;

export const SubtleNote = styled.p`
  text-align: center;
  font-size: 11px;
  color: ${({ theme }) => theme.color.baliHai};
  margin: 0 0 16px;
`;

export const RowStack = styled.div`
  @media ${breakpoints.downSmPlus} {
    margin-top: 4px;
  }
`;
